import I18n from 'i18n-js';
import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';

import PaginationLink from './pagination_link';

const LEFT_GAP = 'previous_ellipsis';
const RIGHT_GAP = 'next_ellipsis';
const REL_FIRST = 'first';
const REL_NEXT = 'next';
const REL_PREV = 'prev';
const REL_NOFOLLOW = 'nofollow';

const range = function range(from, to) {
  const array = [];
  for (let i = from; i <= to; i++) {
    array.push(i);
  }
  return array;
};

class Pagination extends React.Component {
  static get defaultProps() {
    return {
      totalPages: 0,
      paginationWindow: 2,
      paginationPathname: '',
    };
  }

  constructor(props) {
    super(props);
    this.state = {};
    this.changePage = this.changePage.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState({ currentPage: nextProps.currentPage });
  }

  get currentPage() {
    return this.state.currentPage || this.props.currentPage;
  }

  validPage(page) {
    return page !== this.currentPage;
  }

  changePage(page, event) {
    if (!this.props.changePage) { return; }

    event.preventDefault();
    if (!this.validPage(page)) { return; }

    this.setState({ currentPage: page });
    this.props.changePage(page);
  }

  get window() {
    return this.props.window || window;
  }

  get middleRange() {
    let windowFrom = this.currentPage - this.props.paginationWindow;
    let windowTo = this.currentPage + this.props.paginationWindow;
    const { totalPages } = this.props;

    if (windowTo > totalPages) {
      windowFrom -= windowTo - totalPages;
      windowTo = totalPages;
    }

    if (windowFrom < 1) {
      windowTo += 1 - windowFrom;
    }

    return range(
      Math.max(1, windowFrom + 1),
      Math.min(windowTo, totalPages),
    );
  }

  get leftRange() {
    if (this.props.paginationWindow < this.middleRange[0]) {
      return [1, LEFT_GAP]; // display first page and gap only
    }

    // display all pages from beginning to middle range
    return range(1, this.middleRange[0] - 1);
  }

  get rightRange() {
    const { totalPages } = this.props;
    const endOfMiddleRange = this.middleRange[this.middleRange.length - 1];

    if (totalPages > endOfMiddleRange) {
      return [RIGHT_GAP]; // display gap only
    }

    // display all pages from middle range to end
    return range(endOfMiddleRange + 1, totalPages);
  }

  get pageNumbers() {
    return this.leftRange.concat(this.middleRange).concat(this.rightRange);
  }

  renderEllipsisLink(pageNum) {
    const key = `pagination-gap-${pageNum}`;
    const classes = classNames(
      'pagination__page',
      'pagination__page--gap',
      { 'pagination__page--before': pageNum === LEFT_GAP },
      { 'pagination__page--after': pageNum === RIGHT_GAP },
    );

    return (
      <li
        className={classes}
        key={key}
      >
        <span className="pagination__page__ellipsis" aria-hidden="true" />
        <span className="pagination__page__gap-label">
          {I18n.t('discovery.paginationGap')}
        </span>
      </li>
    );
  }

  renderPageNumberLink(pageNum) {
    const key = `pagination-page-${pageNum}`;
    const classes = classNames(
      'pagination__page',
      'pagination__page--number',
      { 'pagination__page--active': pageNum === this.currentPage },
      { 'pagination__page--before': pageNum < this.currentPage },
      { 'pagination__page--after': pageNum > this.currentPage },
    );

    const rel = classNames({
      [REL_FIRST]: pageNum === 1,
      [REL_NOFOLLOW]: this.props.noFollow,
    });

    return (
      <li key={key} className={classes}>
        <PaginationLink
          page={pageNum}
          changePage={this.changePage}
          rel={rel}
          paginationPathname={this.props.paginationPathname}
          window={this.window}
        >
          {pageNum}
        </PaginationLink>
      </li>
    );
  }

  renderPreviousButton() {
    if (this.currentPage === 1) { return null; }

    const newPage = this.currentPage - 1;
    const rel = classNames(REL_PREV, { [REL_NOFOLLOW]: this.props.noFollow });
    return (
      <li key="previous" className="pagination__page pagination__page--previous">
        <PaginationLink
          page={newPage}
          changePage={this.changePage}
          rel={rel}
          paginationPathname={this.props.paginationPathname}
          window={this.window}
        >
          {I18n.t('discovery.previous')}
        </PaginationLink>
      </li>
    );
  }

  renderNextButton() {
    if (this.currentPage === this.props.totalPages) { return null; }

    const newPage = this.currentPage + 1;
    const rel = classNames(REL_NEXT, { [REL_NOFOLLOW]: this.props.noFollow });
    return (
      <li key="next" className="pagination__page pagination__page--next">
        <PaginationLink
          page={newPage}
          changePage={this.changePage}
          rel={rel}
          paginationPathname={this.props.paginationPathname}
          window={this.window}
        >
          {I18n.t('discovery.next')}
        </PaginationLink>
      </li>
    );
  }

  renderPages() {
    return this.pageNumbers.map((pageNum) => {
      if (pageNum === LEFT_GAP || pageNum === RIGHT_GAP) {
        return this.renderEllipsisLink(pageNum);
      }
      return this.renderPageNumberLink(pageNum);
    });
  }

  render() {
    if (this.props.totalPages <= 1) { return null; }

    return (
      <div className="pagination-container">
        <ul>
          {this.renderPreviousButton()}
          {this.renderPages()}
          {this.renderNextButton()}
        </ul>
      </div>
    );
  }
}

Pagination.propTypes = {
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  changePage: PropTypes.func,
  window: PropTypes.object,
  paginationWindow: PropTypes.number,
  paginationPathname: PropTypes.string,
  noFollow: PropTypes.bool,
};

export default Pagination;
